// SEO-friendly URL generation for category-based car listings

export interface CategorySeoData {
  slug: string;
  name: string;
  name_fr: string;
  name_ar: string;
  description: string;
  description_fr: string;
  description_ar: string;
}

// Category slug mappings for different languages
export const categorySlugs: Record<string, CategorySeoData> = {
  'cabriolet': {
    slug: 'cabriolet',
    name: 'Cabriolet',
    name_fr: 'Cabriolet',
    name_ar: 'قابلة للتحويل',
    description: 'Open-top vehicles for scenic drives and special occasions',
    description_fr: 'Véhicules découverts pour les trajets panoramiques et occasions spéciales',
    description_ar: 'مركبات مكشوفة للرحلات الخلابة والمناسبات الخاصة'
  },
  'compact': {
    slug: 'compact',
    name: 'Compact',
    name_fr: 'Compacte',
    name_ar: 'مدمجة',
    description: 'Small and efficient cars perfect for city driving',
    description_fr: 'Voitures petites et efficaces parfaites pour la conduite en ville',
    description_ar: 'سيارات صغيرة وفعالة مثالية للقيادة في المدينة'
  },
  'electric': {
    slug: 'electric',
    name: 'Electric',
    name_fr: 'Électrique',
    name_ar: 'كهربائية',
    description: 'Eco-friendly electric vehicles for sustainable travel',
    description_fr: 'Véhicules électriques écologiques pour un voyage durable',
    description_ar: 'مركبات كهربائية صديقة للبيئة للسفر المستدام'
  },
  'large': {
    slug: 'large',
    name: 'Large',
    name_fr: 'Grande',
    name_ar: 'كبيرة',
    description: 'Spacious vehicles perfect for family trips and group travel',
    description_fr: 'Véhicules spacieux parfaits pour les voyages en famille et en groupe',
    description_ar: 'مركبات واسعة مثالية للرحلات العائلية والسفر الجماعي'
  },
  'luxury': {
    slug: 'luxury',
    name: 'Luxury',
    name_fr: 'Luxe',
    name_ar: 'فاخرة',
    description: 'Premium vehicles with top-tier comfort and features',
    description_fr: 'Véhicules haut de gamme avec confort et fonctionnalités de premier niveau',
    description_ar: 'مركبات فاخرة بأعلى مستويات الراحة والميزات'
  },
  'medium': {
    slug: 'medium',
    name: 'Medium',
    name_fr: 'Moyenne',
    name_ar: 'متوسطة',
    description: 'Mid-size vehicles offering comfort and efficiency',
    description_fr: 'Véhicules de taille moyenne offrant confort et efficacité',
    description_ar: 'مركبات متوسطة الحجم توفر الراحة والكفاءة'
  },
  'pickup': {
    slug: 'pickup',
    name: 'Pickup',
    name_fr: 'Pick-up',
    name_ar: 'بيك أب',
    description: 'Robust pickup trucks for work and adventure',
    description_fr: 'Pick-up robustes pour le travail et l\'aventure',
    description_ar: 'شاحنات صغيرة قوية للعمل والمغامرة'
  },
  'sport': {
    slug: 'sport',
    name: 'Sport',
    name_fr: 'Sport',
    name_ar: 'رياضية',
    description: 'High-performance sports cars for thrilling drives',
    description_fr: 'Voitures de sport haute performance pour des conduites palpitantes',
    description_ar: 'سيارات رياضية عالية الأداء للقيادة المثيرة'
  },
  'suv': {
    slug: 'suv',
    name: 'SUV',
    name_fr: 'SUV',
    name_ar: 'دفع رباعي',
    description: 'Versatile SUVs perfect for all terrains and weather conditions',
    description_fr: 'SUV polyvalents parfaits pour tous terrains et conditions météorologiques',
    description_ar: 'مركبات رياضية متعددة الاستخدامات مثالية لجميع التضاريس والظروف الجوية'
  },
  'berline': {
    slug: 'berline',
    name: 'Sedan',
    name_fr: 'Berline',
    name_ar: 'سيدان',
    description: 'Elegant sedans perfect for business and comfortable city driving',
    description_fr: 'Berlines élégantes parfaites pour les affaires et la conduite confortable en ville',
    description_ar: 'سيارات سيدان أنيقة مثالية للأعمال والقيادة المريحة في المدينة'
  }
};

/**
 * Generate SEO-friendly URL path for category-based car listing
 */
export function generateCategoryUrl(categorySlug: string, language: 'fr' | 'en' | 'ar'): string {
  const category = categorySlugs[categorySlug.toLowerCase()];
  
  // If we have a predefined category, use it
  if (category) {
    const categoryName = category.slug;
    
    switch (language) {
      case 'fr':
        return `/category/location-voiture-${categoryName}`;
      case 'en':
        return `/category/rent-cars-${categoryName}`;
      case 'ar':
        return `/category/تأجير-سيارات-${categoryName}`;
      default:
        return `/category/location-voiture-${categoryName}`;
    }
  }
  
  // For new categories not in our predefined list, generate URL dynamically
  const cleanSlug = categorySlug
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  switch (language) {
    case 'fr':
      return `/category/location-voiture-${cleanSlug}`;
    case 'en':
      return `/category/rent-cars-${cleanSlug}`;
    case 'ar':
      return `/category/تأجير-سيارات-${cleanSlug}`;
    default:
      return `/category/location-voiture-${cleanSlug}`;
  }
}

/**
 * Extract category slug from SEO URL
 */
export function extractCategoryFromUrl(pathname: string): string | null {
  // Match French pattern: /category/location-voiture-{category}
  const frMatch = pathname.match(/\/category\/location-voiture-(.+)/);
  if (frMatch) return frMatch[1];
  
  // Match English pattern: /category/rent-cars-{category}
  const enMatch = pathname.match(/\/category\/rent-cars-(.+)/);
  if (enMatch) return enMatch[1];
  
  // Match Arabic pattern: /category/تأجير-سيارات-{category}
  const arMatch = pathname.match(/\/category\/تأجير-سيارات-(.+)/);
  if (arMatch) return arMatch[1];
  
  return null;
}

/**
 * Generate SEO-optimized title for category page
 */
export function generateCategoryTitle(categorySlug: string, language: 'fr' | 'en' | 'ar'): string {
  const category = categorySlugs[categorySlug.toLowerCase()];
  if (!category) {
    return language === 'fr' ? 'Location de Voitures' : 
           language === 'en' ? 'Car Rental' : 
           'تأجير السيارات';
  }

  const categoryName = language === 'ar' ? category.name_ar : 
                      language === 'fr' ? category.name_fr : 
                      category.name;
  
  switch (language) {
    case 'fr':
      return `Location de Voitures ${categoryName} au Maroc | KiraCar`;
    case 'en':
      return `${categoryName} Car Rental in Morocco | KiraCar`;
    case 'ar':
      return `تأجير سيارات ${categoryName} في المغرب | كيراكار`;
    default:
      return `Location de Voitures ${categoryName} au Maroc | KiraCar`;
  }
}

/**
 * Generate SEO-optimized description for category page
 */
export function generateCategoryDescription(categorySlug: string, language: 'fr' | 'en' | 'ar'): string {
  const category = categorySlugs[categorySlug.toLowerCase()];
  if (!category) {
    return language === 'fr' ? 'Louez une voiture au Maroc avec KiraCar' : 
           language === 'en' ? 'Rent a car in Morocco with KiraCar' : 
           'استأجر سيارة في المغرب مع كيراكار';
  }

  const categoryName = language === 'ar' ? category.name_ar : 
                      language === 'fr' ? category.name_fr : 
                      category.name;

  const description = language === 'ar' ? category.description_ar : 
                     language === 'fr' ? category.description_fr : 
                     category.description;
  
  switch (language) {
    case 'fr':
      return `${description} Louez des voitures ${categoryName.toLowerCase()} au Maroc avec KiraCar. Prix compétitifs, réservation facile et service de qualité garanti.`;
    case 'en':
      return `${description} Rent ${categoryName.toLowerCase()} cars in Morocco with KiraCar. Competitive prices, easy booking and guaranteed quality service.`;
    case 'ar':
      return `${description} استأجر سيارات ${categoryName} في المغرب مع كيراكار. أسعار تنافسية وحجز سهل وخدمة عالية الجودة مضمونة.`;
    default:
      return `${description} Louez des voitures ${categoryName.toLowerCase()} au Maroc avec KiraCar.`;
  }
}

/**
 * Generate SEO-optimized keywords for category page
 */
export function generateCategoryKeywords(categorySlug: string, language: 'fr' | 'en' | 'ar'): string {
  const category = categorySlugs[categorySlug.toLowerCase()];
  if (!category) {
    return language === 'fr' ? 'location voiture maroc, rent car morocco' : 
           language === 'en' ? 'car rental morocco, rent car morocco' : 
           'تأجير سيارات المغرب, كراء السيارات المغرب';
  }

  const categoryName = language === 'ar' ? category.name_ar : 
                      language === 'fr' ? category.name_fr : 
                      category.name;
  const categorySlugLower = category.slug.toLowerCase();
  
  switch (language) {
    case 'fr':
      return `location voiture ${categorySlugLower}, location ${categoryName.toLowerCase()} maroc, voiture ${categoryName.toLowerCase()} location, kiracar ${categorySlugLower}, location auto ${categoryName.toLowerCase()}, rent car ${categorySlugLower}`;
    case 'en':
      return `${categorySlugLower} car rental, rent ${categoryName.toLowerCase()} car, ${categoryName.toLowerCase()} cars morocco, kiracar ${categorySlugLower}, ${categorySlugLower} vehicle rental, hire ${categoryName.toLowerCase()} car`;
    case 'ar':
      return `تأجير سيارات ${categoryName}, كراء سيارة ${categoryName}, تأجير ${categoryName} المغرب, كيراكار ${categoryName}, استئجار سيارة ${categoryName}`;
    default:
      return `location voiture ${categorySlugLower}, rent car ${categoryName.toLowerCase()}, kiracar ${categorySlugLower}`;
  }
}

/**
 * Check if a URL path is a category-based URL
 */
export function isCategoryUrl(pathname: string): boolean {
  return extractCategoryFromUrl(pathname) !== null;
}

/**
 * Get all available category slugs
 */
export function getAllCategorySlugs(): string[] {
  return Object.keys(categorySlugs);
}

/**
 * Get category data by slug
 */
export function getCategoryBySlug(slug: string): CategorySeoData | null {
  // First check if we have a predefined category
  const predefinedCategory = categorySlugs[slug.toLowerCase()];
  if (predefinedCategory) {
    return predefinedCategory;
  }
  
  // For new categories, return a generic category data
  // This allows the system to work with any category from the database
  return {
    slug: slug.toLowerCase(),
    name: slug.charAt(0).toUpperCase() + slug.slice(1),
    name_fr: slug.charAt(0).toUpperCase() + slug.slice(1),
    name_ar: slug.charAt(0).toUpperCase() + slug.slice(1),
    description: `Browse our selection of ${slug} cars`,
    description_fr: `Parcourez notre sélection de voitures ${slug}`,
    description_ar: `تصفح مجموعتنا من سيارات ${slug}`
  };
}

/**
 * Generate alternate language URLs for category pages (for hreflang)
 */
export function generateCategoryAlternateUrls(categorySlug: string, baseUrl: string = 'https://kiracar.com'): Record<string, string> {
  const category = categorySlugs[categorySlug.toLowerCase()];
  if (!category) {
    return {};
  }

  return {
    'fr': `${baseUrl}/fr${generateCategoryUrl(categorySlug, 'fr')}`,
    'en': `${baseUrl}/en${generateCategoryUrl(categorySlug, 'en')}`,
    'ar': `${baseUrl}/ar${generateCategoryUrl(categorySlug, 'ar')}`
  };
}
